/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable arrow-body-style */
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TimeFormatPipe } from 'app/core/pipes/timeFormat.pipe';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { rrulestr } from 'rrule';
import { JobDate, RRuleDateOptions } from './rrule-date.type';
import * as moment from 'moment';
import { HotToastService } from '@ngneat/hot-toast';


@Component({
  selector: 'app-rrule-date',
  templateUrl: './rrule-date.component.html',
  styleUrls: ['./rrule-date.component.scss']
})
export class RruleDateComponent implements OnInit, OnChanges {

  @Input() dateGroup: UntypedFormGroup;
  @Input() options: RRuleDateOptions;
  @Input() jobs: JobDate[];
  @Input() isDisabled?: boolean = false;

  initComponent: boolean = false;
  enableMeridian = false;
  dateArray: UntypedFormArray;
  dateFormat: string = 'DD/MM/YYYY';
  timeFormat: string = 'HH:mm A';
  timePickerModel: any = {};
  timepickerStart: any[] = [];
  timepickerEnd: any[] = [];
  currentPage: number = 1;
  limit: number = 5;
  templateArray: any[] = new Array(this.limit);

  isLoading: boolean = false;
  processedCount: number = 0;
  totalCount: number = 0;
  private currentBatchId: string | null = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private _timeFormatPipe: TimeFormatPipe,
    private _toast: HotToastService,
    public utilityService: UtilityService
  ) { }

  get names(): UntypedFormArray {
    return this.dateGroup.controls.dates as UntypedFormArray;
  }

  get namesArray(): any[] {
    const arr = new Array(this.names);
    return arr.slice((this.currentPage * this.limit) - this.limit, (this.currentPage * this.limit) + 1);
  }

  get visibleControls(): any[] {
    return this.names.controls
      .filter(control => control.value.shouldDisplay)
      .map((control, index) => ({ control, originalIndex: this.names.controls.indexOf(control) }));
  }

  get optimizedTemplateArray(): number[] {
    const visibleIndices = this.names.controls
      .map((control, index) => ({ control, index }))
      .filter(item => item.control.value.shouldDisplay)
      .map(item => item.index);
    const start = (this.currentPage - 1) * this.limit;
    const end = Math.min(start + this.limit, visibleIndices.length);
    return visibleIndices.slice(start, end);
  }

  get totalPage(): number {
    if (!this.names?.controls.length) return 1;
    const visibleCount = this.names.controls.filter(control => control.value.shouldDisplay).length;
    return visibleCount === 0 ? 1 : Math.ceil(visibleCount / this.limit);
  }

  nextPage(): void{
    if(this.currentPage < this.totalPage){
      this.currentPage++;
    }
  }

  prevPage(): void{
    if(this.currentPage > 1){
      this.currentPage--;
    }
  }

  addDate(data: any, index: number): void {
    const isPastDate = moment(data?.date).isBefore(moment(), 'day');
    this.timepickerStart[index] = moment(data?.startTime, this.timeFormat);
    this.timepickerEnd[index] = moment(data?.endTime, this.timeFormat);
    const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
 
    const group = this._fb.group({
      date: new UntypedFormControl(data?.date, [Validators.required]),
      startTime: new UntypedFormControl(data?.startTime, [Validators.required]),
      endTime: new UntypedFormControl(data?.endTime, [Validators.required]),
      startTimeStepperMin: new UntypedFormControl(15),
      endTimeStepperMin: new UntypedFormControl(15),
      shouldDisplay: new UntypedFormControl(!isPastDate)
    });

    dateArray.push(group);
  }

  // Custom Validator
  minDateValidator(index: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null =>
    {
      let checked = false;
      const date = moment(control.value, this.timeFormat).format(' HH:mm:ss');
      const startDate = moment(this.timepickerStart[index]).format(' HH:mm:ss');
      if(moment(date, ' HH:mm:ss').isBefore(moment(startDate, ' HH:mm:ss'))){
        checked = true;
      }
      return checked ? null : {error: {
        'CUSTOM': 'required'
      }};
    };
  }

  dateValidator(index: number): boolean {
      let checked = false;
      const endDate = moment(this.timepickerEnd[index]).format(' HH:mm:ss');
      const startDate = moment(this.timepickerStart[index]).format(' HH:mm:ss');
      if(moment(endDate, ' HH:mm:ss').isBefore(moment(startDate, ' HH:mm:ss'))){
        checked = true;
      }
      if(checked){
        this.dateGroup.setErrors({'startDate': true});
      }
      return checked;
  }

  minTimeError(group: UntypedFormGroup,name: string): string{
    if(group.controls[name]?.invalid && group.controls[name]?.touched){
        return 'field.min_time.error.message';
    }
    return '';
  }

  removeDate(index: number): void {
    const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
    const removeIndex = this.currentPage === 1 ? index : (((this.currentPage -1) * this.limit) + index);
    this.timepickerEnd.splice(removeIndex, 1);
    this.timepickerStart.splice(removeIndex, 1);
    dateArray.removeAt(removeIndex);
  }

  setStartTimeControl(controlName: string, index: number): void {
    const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
    const currentFormControl = dateArray.controls[index];
    if(this.timepickerStart[index] && (currentFormControl?.get(controlName).value || currentFormControl?.get(controlName).value === null )){
      currentFormControl?.get(controlName).setValue(moment(this.timepickerStart[index]).format(this.timeFormat));
      const stepperMin = this.utilityService.getNextQuarterStepperMin(moment(this.timepickerStart[index]).clone());
      currentFormControl.get('startTimeStepperMin').setValue(stepperMin);
    }else{
      currentFormControl?.get(controlName).setValue(null);
    }
  }

  setEndTimeControl(controlName: string, index: number): void {
    const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
    const currentFormControl = dateArray.controls[index];
    if(this.timepickerEnd[index] && (currentFormControl?.get(controlName).value || currentFormControl?.get(controlName).value === null )){
      currentFormControl?.get(controlName).setValue(moment(this.timepickerEnd[index]).format(this.timeFormat));
      const stepperMin = this.utilityService.getNextQuarterStepperMin(moment(this.timepickerEnd[index]).clone());
      currentFormControl.get('endTimeStepperMin').setValue(stepperMin);
    }else{
      currentFormControl?.get(controlName).setValue(null);
    }
  }

  private cancelPreviousBatch(): void {
    this.currentBatchId = null;
    this.isLoading = false;
    this.processedCount = 0;
  }

  setDates(rrule: string): void {
    if (rrule) {
      // Cancel any ongoing process first
      this.cancelPreviousBatch();
      
      // Start new process
      this.currentBatchId = Date.now().toString();
      const batchId = this.currentBatchId;

      this.isLoading = true;
      this.processedCount = 0;
      this.currentPage = 1;
      this.timepickerStart = [];
      this.timepickerEnd = [];
      const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
      dateArray.clear();
      const allDates = rrulestr(rrule).all();
      this.totalCount = allDates.length;
      this.processRRuleDatesInBatches(allDates, 0, 50, batchId);
      this.dateGroup.markAllAsTouched();
    }
  }

  processRRuleDatesInBatches(dates: Date[], startIndex: number, batchSize: number, batchId: string): void {
    // Skip if this batch is no longer current
    if (batchId !== this.currentBatchId) {
      return;
    }

    const endIndex = Math.min(startIndex + batchSize, dates.length);
    
    requestAnimationFrame(() => {
      // Skip if batch was cancelled
      if (batchId !== this.currentBatchId) {
        return;
      }

      for (let index = startIndex; index < endIndex; index++) {
        // Skip if batch was cancelled during processing
        if (batchId !== this.currentBatchId) {
          return;
        }
        this.addDate({
          date: moment.utc(dates[index]),
          startTime: this.options.startTime,
          endTime: this.options.endTime
        }, index);
        this.processedCount++;
      }

      if (endIndex < dates.length && batchId === this.currentBatchId) {
        setTimeout(() => {
          this.processRRuleDatesInBatches(dates, endIndex, batchSize, batchId);
        }, 0);
      } else if (batchId === this.currentBatchId) {
        this.isLoading = false;
      }
    });
  }

  setJobDates(jobs: any): void {
    if (jobs?.length > 0) {
      // Cancel any ongoing process first
      this.cancelPreviousBatch();
      
      // Start new process
      this.currentBatchId = Date.now().toString();
      const batchId = this.currentBatchId;

      this.isLoading = true;
      this.processedCount = 0;
      this.totalCount = jobs.length;
      const dateArray = this.dateGroup.get('dates') as UntypedFormArray;
      dateArray.clear();
      this.timepickerStart = [];
      this.timepickerEnd = [];
      this.processJobsInBatches(jobs, 0, 100, batchId);
      this.dateGroup.markAllAsTouched();
    }
    this.initComponent = true;
  }

  processJobsInBatches(jobs: any[], startIndex: number, batchSize: number, batchId: string): void {
    // Skip if this batch is no longer current
    if (batchId !== this.currentBatchId) {
      return;
    }

    const endIndex = Math.min(startIndex + batchSize, jobs.length);
    
    requestAnimationFrame(() => {
      // Skip if batch was cancelled
      if (batchId !== this.currentBatchId) {
        return;
      }

      for (let index = startIndex; index < endIndex; index++) {
        // Skip if batch was cancelled during processing
        if (batchId !== this.currentBatchId) {
          return;
        }
        this.addDate(jobs[index], index);
        this.processedCount++;
      }

      if (endIndex < jobs.length && batchId === this.currentBatchId) {
        setTimeout(() => {
          this.processJobsInBatches(jobs, endIndex, batchSize, batchId);
        }, 0);
      } else if (batchId === this.currentBatchId) {
        this.isLoading = false;
      }
    });
  }

  ngOnInit(): void {
    this.dateFormat = this.utilityService.dateFormat + ' dddd';
    this.timeFormat = this.utilityService.timeFormat;
    this.enableMeridian = this.utilityService.showMeridian;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if('options' in changes){
      if(this.options?.rrule && (this.initComponent || (!this.jobs && !this.options.hasJob)) && (changes.options.currentValue?.rrule !== changes.options.previousValue?.rrule)){
        const dateArray =  this.dateGroup.get('dates') as UntypedFormArray;
        dateArray.clear();
        this.isLoading = false;        
        this.setDates(this.options.rrule);
      }
    }
    if('jobs' in changes){
      this.jobs = changes.jobs.currentValue;
      this.isLoading = false; 
      this.setJobDates(this.jobs);
    }
  }

}
